import {
  available,
  I64_MAX,
  isI64,
  isNonNegativeI64,
  isU64,
  isU128,
  PRICE_SCALE_D9,
  unavailable,
} from './integer.js';
import type { PoolMathResult } from './types.js';

export interface CheckedPositionAccountingState {
  balanceD6: bigint;
  remainingCostBasisNetD6: bigint;
  claimRightsBalanceD6: bigint;
  realizedPnlD6: bigint;
  realizedCostBasisD6: bigint;
}

export type CheckedPositionTrade =
  | {
      direction: 'buy';
      tokenAmountD6: bigint;
      walletDebitD6: bigint;
      isAmm: boolean;
    }
  | {
      direction: 'sell';
      tokenAmountD6: bigint;
      netQuoteProceedsD6: bigint;
      isAmm: boolean;
    };

export interface CheckedPositionAccountingResult extends CheckedPositionAccountingState {
  releasedCostBasisD6: bigint;
  realizedPnlDeltaD6: bigint;
  avgEntryPriceD9: bigint;
}

/** Both P&L ledgers share balances and release cost basis independently. */
export interface CheckedPositionGrossNetAccountingState {
  balanceD6: bigint;
  claimRightsBalanceD6: bigint;
  remainingCostBasisGrossD6: bigint;
  remainingCostBasisNetD6: bigint;
  realizedPnlGrossD6: bigint;
  realizedPnlNetD6: bigint;
  realizedCostBasisGrossD6: bigint;
  realizedCostBasisNetD6: bigint;
}

export type CheckedPositionGrossNetTrade =
  | {
      direction: 'buy';
      tokenAmountD6: bigint;
      grossQuoteInputD6: bigint;
      netWalletDebitD6: bigint;
      isAmm: boolean;
    }
  | {
      direction: 'sell';
      tokenAmountD6: bigint;
      feeExcludedQuoteProceedsD6: bigint;
      netQuoteProceedsD6: bigint;
      isAmm: boolean;
    };

export interface CheckedPositionPnlAccountingLeg {
  remainingCostBasisD6: bigint;
  realizedPnlD6: bigint;
  realizedCostBasisD6: bigint;
  releasedCostBasisD6: bigint;
  realizedPnlDeltaD6: bigint;
}

export interface CheckedPositionGrossNetAccountingResult {
  balanceD6: bigint;
  claimRightsBalanceD6: bigint;
  gross: CheckedPositionPnlAccountingLeg;
  net: CheckedPositionPnlAccountingLeg;
  netAvgEntryPriceD9: bigint;
}

/** Gross/net-neutral D6 basis divided by D6 balance, floored to D9. */
export function checkedAverageEntryPriceD9(
  remainingCostBasisD6: bigint,
  balanceD6: bigint,
): PoolMathResult<bigint> {
  if (!isNonNegativeI64(remainingCostBasisD6) || !isU64(balanceD6)) {
    return unavailable('invalid_amount');
  }
  if (balanceD6 === 0n) {
    return remainingCostBasisD6 === 0n ? available(0n) : unavailable('invalid_amount');
  }
  const numerator = remainingCostBasisD6 * PRICE_SCALE_D9;
  if (!isU128(numerator)) return unavailable('invalid_amount');
  const average = numerator / balanceD6;
  return isU128(average) ? available(average) : unavailable('invalid_amount');
}

/** Applies one trade to canonical signed-BIGINT position accounting. */
export function applyCheckedPositionTrade(
  state: CheckedPositionAccountingState,
  trade: CheckedPositionTrade,
): PoolMathResult<CheckedPositionAccountingResult> {
  if (!validState(state) || !positiveI64(trade.tokenAmountD6)) {
    return unavailable('malformed_position_accounting');
  }
  return trade.direction === 'buy' ? applyBuy(state, trade) : applySell(state, trade);
}

/**
 * Applies the same trade to fee-excluded and executable accounting in lockstep.
 * The balances and claim rights must agree; only cash amounts differ by P&L view.
 */
export function applyCheckedPositionGrossNetTrade(
  state: CheckedPositionGrossNetAccountingState,
  trade: CheckedPositionGrossNetTrade,
): PoolMathResult<CheckedPositionGrossNetAccountingResult> {
  const gross = applyCheckedPositionTrade(grossState(state), grossTrade(trade));
  if (!gross.available) return gross;
  const net = applyCheckedPositionTrade(netState(state), netTrade(trade));
  if (!net.available) return net;
  if (
    gross.value.balanceD6 !== net.value.balanceD6 ||
    gross.value.claimRightsBalanceD6 !== net.value.claimRightsBalanceD6
  ) {
    return unavailable('malformed_position_accounting');
  }

  return available({
    balanceD6: net.value.balanceD6,
    claimRightsBalanceD6: net.value.claimRightsBalanceD6,
    gross: accountingLeg(gross.value),
    net: accountingLeg(net.value),
    netAvgEntryPriceD9: net.value.avgEntryPriceD9,
  });
}

function applyBuy(
  state: CheckedPositionAccountingState,
  trade: Extract<CheckedPositionTrade, { direction: 'buy' }>,
): PoolMathResult<CheckedPositionAccountingResult> {
  if (!positiveI64(trade.walletDebitD6)) {
    return unavailable('malformed_position_accounting');
  }
  const balanceD6 = state.balanceD6 + trade.tokenAmountD6;
  const remainingCostBasisNetD6 = state.remainingCostBasisNetD6 + trade.walletDebitD6;
  const claimRightsBalanceD6 = trade.isAmm
    ? state.claimRightsBalanceD6
    : state.claimRightsBalanceD6 + trade.tokenAmountD6;
  const avgEntryPriceD9 = checkedAverageEntryPriceD9(remainingCostBasisNetD6, balanceD6);
  if (
    !isNonNegativeI64(balanceD6) ||
    !isNonNegativeI64(remainingCostBasisNetD6) ||
    !isNonNegativeI64(claimRightsBalanceD6) ||
    !avgEntryPriceD9.available
  ) {
    return unavailable('accounting_overflow');
  }
  return available({
    balanceD6,
    remainingCostBasisNetD6,
    claimRightsBalanceD6,
    realizedPnlD6: state.realizedPnlD6,
    realizedCostBasisD6: state.realizedCostBasisD6,
    releasedCostBasisD6: 0n,
    realizedPnlDeltaD6: 0n,
    avgEntryPriceD9: avgEntryPriceD9.value,
  });
}

function applySell(
  state: CheckedPositionAccountingState,
  trade: Extract<CheckedPositionTrade, { direction: 'sell' }>,
): PoolMathResult<CheckedPositionAccountingResult> {
  if (!isNonNegativeI64(trade.netQuoteProceedsD6)) {
    return unavailable('malformed_position_accounting');
  }
  if (trade.tokenAmountD6 > state.balanceD6) return unavailable('balance_oversold');
  if (!trade.isAmm && trade.tokenAmountD6 > state.claimRightsBalanceD6) {
    return unavailable('claim_rights_short');
  }
  const releaseNumerator = state.remainingCostBasisNetD6 * trade.tokenAmountD6;
  if (!isU128(releaseNumerator)) return unavailable('accounting_overflow');
  const releasedCostBasisD6 = releaseNumerator / state.balanceD6;
  const balanceD6 = state.balanceD6 - trade.tokenAmountD6;
  const remainingCostBasisNetD6 = state.remainingCostBasisNetD6 - releasedCostBasisD6;
  const realizedPnlDeltaD6 = trade.netQuoteProceedsD6 - releasedCostBasisD6;
  const realizedPnlD6 = state.realizedPnlD6 + realizedPnlDeltaD6;
  const realizedCostBasisD6 = state.realizedCostBasisD6 + releasedCostBasisD6;
  const claimRightsBalanceD6 = trade.isAmm
    ? state.claimRightsBalanceD6
    : state.claimRightsBalanceD6 - trade.tokenAmountD6;
  const avgEntryPriceD9 = checkedAverageEntryPriceD9(remainingCostBasisNetD6, balanceD6);
  if (
    !isNonNegativeI64(remainingCostBasisNetD6) ||
    !isI64(realizedPnlDeltaD6) ||
    !isI64(realizedPnlD6) ||
    !isNonNegativeI64(realizedCostBasisD6) ||
    !isNonNegativeI64(claimRightsBalanceD6) ||
    !avgEntryPriceD9.available
  ) {
    return unavailable('accounting_overflow');
  }
  return available({
    balanceD6,
    remainingCostBasisNetD6,
    claimRightsBalanceD6,
    realizedPnlD6,
    realizedCostBasisD6,
    releasedCostBasisD6,
    realizedPnlDeltaD6,
    avgEntryPriceD9: avgEntryPriceD9.value,
  });
}

function validState(state: CheckedPositionAccountingState): boolean {
  return (
    isNonNegativeI64(state.balanceD6) &&
    isNonNegativeI64(state.remainingCostBasisNetD6) &&
    isNonNegativeI64(state.claimRightsBalanceD6) &&
    isI64(state.realizedPnlD6) &&
    isNonNegativeI64(state.realizedCostBasisD6)
  );
}

function grossState(state: CheckedPositionGrossNetAccountingState): CheckedPositionAccountingState {
  return {
    balanceD6: state.balanceD6,
    remainingCostBasisNetD6: state.remainingCostBasisGrossD6,
    claimRightsBalanceD6: state.claimRightsBalanceD6,
    realizedPnlD6: state.realizedPnlGrossD6,
    realizedCostBasisD6: state.realizedCostBasisGrossD6,
  };
}

function netState(state: CheckedPositionGrossNetAccountingState): CheckedPositionAccountingState {
  return {
    balanceD6: state.balanceD6,
    remainingCostBasisNetD6: state.remainingCostBasisNetD6,
    claimRightsBalanceD6: state.claimRightsBalanceD6,
    realizedPnlD6: state.realizedPnlNetD6,
    realizedCostBasisD6: state.realizedCostBasisNetD6,
  };
}

function grossTrade(trade: CheckedPositionGrossNetTrade): CheckedPositionTrade {
  return trade.direction === 'buy'
    ? {
        direction: 'buy',
        tokenAmountD6: trade.tokenAmountD6,
        walletDebitD6: trade.grossQuoteInputD6,
        isAmm: trade.isAmm,
      }
    : {
        direction: 'sell',
        tokenAmountD6: trade.tokenAmountD6,
        netQuoteProceedsD6: trade.feeExcludedQuoteProceedsD6,
        isAmm: trade.isAmm,
      };
}

function netTrade(trade: CheckedPositionGrossNetTrade): CheckedPositionTrade {
  return trade.direction === 'buy'
    ? {
        direction: 'buy',
        tokenAmountD6: trade.tokenAmountD6,
        walletDebitD6: trade.netWalletDebitD6,
        isAmm: trade.isAmm,
      }
    : {
        direction: 'sell',
        tokenAmountD6: trade.tokenAmountD6,
        netQuoteProceedsD6: trade.netQuoteProceedsD6,
        isAmm: trade.isAmm,
      };
}

function accountingLeg(result: CheckedPositionAccountingResult): CheckedPositionPnlAccountingLeg {
  return {
    remainingCostBasisD6: result.remainingCostBasisNetD6,
    realizedPnlD6: result.realizedPnlD6,
    realizedCostBasisD6: result.realizedCostBasisD6,
    releasedCostBasisD6: result.releasedCostBasisD6,
    realizedPnlDeltaD6: result.realizedPnlDeltaD6,
  };
}

function positiveI64(value: bigint): boolean {
  return value > 0n && value <= I64_MAX;
}
